import { graphqlQuery } from '@/lib/api/graphql';
import type { GraphQLCursorConnection } from '@/lib/graphql-pagination';
import type { DisputeAuditLogEntry } from '@/lib/mock-data/dispute-audit-log-mock-data';
import { generateDisputeAuditLog } from '@/lib/mock-data/dispute-audit-log-mock-data';
import { useCursorPagination } from './useCursorPagination';

// --- GraphQL types ---

interface DisputeTypeAuditLogGraphQLNode {
  auditLogId: number;
  disputeTypeId: number;
  actionType: string | null;
  description: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  metadata: string | null;
  createdByUserName: string | null;
  createdAt: string;
}

interface DisputeTypeAuditLogListGraphQLResponse {
  disputes: {
    disputeTypeAuditLogList: GraphQLCursorConnection<DisputeTypeAuditLogGraphQLNode>;
  };
}

// --- GraphQL query ---

const DISPUTE_TYPE_AUDIT_LOG_LIST_QUERY = `
  query DisputeTypeAuditLogList($first: Int, $after: String, $filter: DisputeTypeAuditLogListFilterInput) {
    disputes {
      disputeTypeAuditLogList(first: $first, after: $after, filter: $filter) {
        edges {
          node {
            auditLogId
            disputeTypeId
            actionType
            description
            fieldName
            oldValue
            newValue
            metadata
            createdByUserName
            createdAt
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const AUDIT_LOG_PAGE_SIZE = 25;

// --- Mapping ---

const actionTypeToEventMap: Record<string, DisputeAuditLogEntry['event']> = {
  created: 'created',
  updated: 'updated',
  activated: 'activated',
  deactivated: 'deactivated',
  deleted: 'deleted',
  workflowchanged: 'workflow_changed',
  configurationchanged: 'configuration_changed',
};

function mapGraphQLNodeToDisputeAuditLogEntry(node: DisputeTypeAuditLogGraphQLNode): DisputeAuditLogEntry {
  const actionName = (node.actionType ?? '').toLowerCase().replace(/\s+/g, '');

  let details: string | undefined;
  if (node.fieldName && node.oldValue && node.newValue) {
    details = `${node.fieldName} changed from '${node.oldValue}' to '${node.newValue}'`;
  } else if (node.fieldName && node.newValue) {
    details = `${node.fieldName} set to '${node.newValue}'`;
  } else {
    details = node.description || undefined;
  }

  return {
    id: String(node.auditLogId),
    date: new Date(node.createdAt),
    event: actionTypeToEventMap[actionName] || 'updated',
    user: { name: node.createdByUserName || 'System', id: '' },
    details,
  };
}

// --- Hook ---

export interface DisputeTypeAuditLogsResult {
  auditLogs: DisputeAuditLogEntry[];
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
}

export function useDisputeTypeAuditLogs(disputeTypeId: string | undefined): DisputeTypeAuditLogsResult {
  const result = useCursorPagination<DisputeTypeAuditLogGraphQLNode, DisputeAuditLogEntry>(
    {
      pageSize: AUDIT_LOG_PAGE_SIZE,
      buildMockData: () => (disputeTypeId ? generateDisputeAuditLog(disputeTypeId) : []),
      executeQuery: async (apiConfig, afterCursor, signal) => {
        if (!disputeTypeId) {
          return { edges: [], pageInfo: { endCursor: null, hasNextPage: false } };
        }
        const variables: Record<string, unknown> = {
          first: AUDIT_LOG_PAGE_SIZE,
          filter: { disputeTypeId: { eq: Number(disputeTypeId) } },
        };
        if (afterCursor) {
          variables.after = afterCursor;
        }
        const response = await graphqlQuery<DisputeTypeAuditLogListGraphQLResponse>(
          apiConfig,
          DISPUTE_TYPE_AUDIT_LOG_LIST_QUERY,
          variables,
          signal,
        );
        return response.disputes.disputeTypeAuditLogList;
      },
      mapNode: mapGraphQLNodeToDisputeAuditLogEntry,
    },
    [disputeTypeId],
  );

  return {
    auditLogs: result.items,
    isLoading: result.isLoading,
    isFetchingMore: result.isFetchingMore,
    hasMore: result.hasMore,
    loadMore: result.loadMore,
    reset: result.reset,
  };
}
